import { uiText, useUiLanguage } from '@/i18n/interface';
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Palette, RotateCcw, Paintbrush, Sparkles } from 'lucide-react';
import { useColorTheme, ColorTheme, ColorPreset } from '@/contexts/ColorThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';

const ColorPicker: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  description?: string;
}> = ({ label, value, onChange, description }) => {
  useUiLanguage();
  const [hue, saturation, lightness] = value.split(' ').map(v => parseInt(v.replace('%', '')));

  const handleHueChange = (newHue: number) => {
    onChange(`${newHue} ${saturation}% ${lightness}%`);
  };

  const handleSaturationChange = (newSaturation: number) => {
    onChange(`${hue} ${newSaturation}% ${lightness}%`);
  };

  const handleLightnessChange = (newLightness: number) => {
    onChange(`${hue} ${saturation}% ${newLightness}%`);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">{label}</Label>
        <div
          className="w-8 h-8 rounded border border-border"
          style={{ backgroundColor: `hsl(${value})` }}
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-center space-x-2">
          <Label className="text-xs text-muted-foreground w-12">{uiText("Hue")}</Label>
          <input
            type="range"
            min="0"
            max="360"
            value={hue}
            onChange={(e) => handleHueChange(parseInt(e.target.value))}
            className="flex-1 h-2 bg-gradient-to-r from-red-500 via-yellow-500 via-green-500 via-cyan-500 via-blue-500 via-purple-500 to-red-500 rounded-lg appearance-none"
          />
          <span className="text-xs text-muted-foreground w-8">{hue}°</span>
        </div>

        <div className="flex items-center space-x-2">
          <Label className="text-xs text-muted-foreground w-12">{uiText("Sat")}</Label>
          <input
            type="range"
            min="0"
            max="100"
            value={saturation}
            onChange={(e) => handleSaturationChange(parseInt(e.target.value))}
            className="flex-1 h-2 bg-gradient-to-r from-gray-400 to-current rounded-lg appearance-none"
            style={{ color: `hsl(${hue} 100% 50%)` }}
          />
          <span className="text-xs text-muted-foreground w-8">{saturation}%</span>
        </div>

        <div className="flex items-center space-x-2">
          <Label className="text-xs text-muted-foreground w-12">{uiText("Light")}</Label>
          <input
            type="range"
            min="0"
            max="100"
            value={lightness}
            onChange={(e) => handleLightnessChange(parseInt(e.target.value))}
            className="flex-1 h-2 bg-gradient-to-r from-black via-current to-white rounded-lg appearance-none"
            style={{ color: `hsl(${hue} ${saturation}% 50%)` }}
          />
          <span className="text-xs text-muted-foreground w-8">{lightness}%</span>
        </div>
      </div>

      {description && (
        <p className="text-xs text-muted-foreground">{description}</p>
      )}
    </div>
  );
};

const PresetCard: React.FC<{
  preset: ColorPreset;
  isActive: boolean;
  onApply: () => void;
}> = ({ preset, isActive, onApply }) => {
  useUiLanguage();
  return (
    <Card className={`cursor-pointer transition-all hover:shadow-md ${isActive ? 'ring-2 ring-dojo-primary' : ''}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm">{uiText(preset.name)}</CardTitle>
          {isActive && <Badge variant="secondary" className="text-xs">{uiText("Active")}</Badge>}
        </div>
        <CardDescription className="text-xs">{preset.description}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex space-x-1 mb-3">
          <div
            className="w-4 h-4 rounded border border-border"
            style={{ backgroundColor: `hsl(${preset.colors.primary})` }}
          />
          <div
            className="w-4 h-4 rounded border border-border"
            style={{ backgroundColor: `hsl(${preset.colors.secondary})` }}
          />
          <div
            className="w-4 h-4 rounded border border-border"
            style={{ backgroundColor: `hsl(${preset.colors.accent})` }}
          />
          <div
            className="w-4 h-4 rounded border border-border"
            style={{ backgroundColor: `hsl(${preset.colors.success})` }}
          />
        </div>
        <Button
          size="sm"
          variant={isActive ? "secondary" : "outline"}
          onClick={onApply}
          className="w-full text-xs"
          disabled={isActive}
        >
          {isActive ? uiText("Applied") : uiText("Apply Theme")}
        </Button>
      </CardContent>
    </Card>
  );
};

const ColorThemeSettings: React.FC = () => {
  useUiLanguage();
  const { profile } = useAuth();
  const {
    currentTheme,
    setCustomTheme,
    applyPreset,
    resetToDefault,
    presets,
    isLoading
  } = useColorTheme();

  const [customTheme, setCustomThemeLocal] = useState<ColorTheme>(currentTheme);
  React.useEffect(()=>setCustomThemeLocal(currentTheme),[currentTheme]);

  if (profile?.role !== 'admin' && profile?.role !== 'super_user') {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{uiText("Access Denied")}</CardTitle>
          <CardDescription>{uiText("Only administrators can access color theme settings.")}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const handleApplyCustom = async () => {
    const serverSaved = await setCustomTheme(customTheme);
    toast({
      title: uiText("Theme Applied"),
      description: serverSaved ? "Your theme has been applied and saved." : "Theme applied and saved on this device. Server synchronisation is unavailable.",
    });
  };

  const handleResetToDefault = async () => {
    await resetToDefault();
    toast({
      title: uiText("Theme Reset"),
      description: uiText("Color theme has been reset to default."),
    });
  };

  const isCurrentPreset = (preset: ColorPreset) => {
    return JSON.stringify(preset.colors) === JSON.stringify(currentTheme);
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{uiText("Loading Color Settings...")}</CardTitle>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Preview Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palette className="h-5 w-5" />
            {uiText("Live Preview")}</CardTitle>
          <CardDescription>{uiText("See how your theme looks in real-time")}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label className="text-xs">{uiText("Primary")}</Label>
              <div className="p-4 rounded-lg bg-dojo-primary text-dojo-primary-foreground">
                <div className="text-sm font-medium">{uiText("Primary Color")}</div>
                <div className="text-xs opacity-90">{uiText("Main brand color")}</div>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">{uiText("Secondary")}</Label>
              <div className="p-4 rounded-lg bg-dojo-secondary text-dojo-secondary-foreground">
                <div className="text-sm font-medium">{uiText("Secondary")}</div>
                <div className="text-xs opacity-90">{uiText("Accent color")}</div>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">{uiText("Success")}</Label>
              <div className="p-4 rounded-lg bg-dojo-success text-dojo-success-foreground">
                <div className="text-sm font-medium">{uiText("Success")}</div>
                <div className="text-xs opacity-90">{uiText("Success states")}</div>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">{uiText("Accent")}</Label>
              <div className="p-4 rounded-lg bg-dojo-accent text-dojo-accent-foreground">
                <div className="text-sm font-medium">{uiText("Accent")}</div>
                <div className="text-xs opacity-90">{uiText("Info & highlights")}</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="presets" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="presets" className="flex items-center gap-2">
            <Sparkles className="h-4 w-4" />
            {uiText("Preset Themes")}</TabsTrigger>
          <TabsTrigger value="custom" className="flex items-center gap-2">
            <Paintbrush className="h-4 w-4" />
            {uiText("Custom Colors")}</TabsTrigger>
        </TabsList>

        <TabsContent value="presets" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{uiText("Choose a Preset Theme")}</CardTitle>
              <CardDescription>
                {uiText("Select from pre-designed color themes optimized for martial arts dojos")}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {presets.map((preset) => (
                  <PresetCard
                    key={preset.id}
                    preset={preset}
                    isActive={isCurrentPreset(preset)}
                    onApply={() => applyPreset(preset.id)}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="custom" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{uiText("Custom Color Settings")}</CardTitle>
              <CardDescription>
                {uiText("Create your own unique color theme for your dojo")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <ColorPicker
                  label={uiText("Primary Color")}
                  value={customTheme.primary}
                  onChange={(value) => setCustomThemeLocal({...customTheme, primary: value})}
                  description={uiText("Main brand color used throughout the application")}
                />

                <ColorPicker
                  label={uiText("Primary Hover")}
                  value={customTheme.primaryHover}
                  onChange={(value) => setCustomThemeLocal({...customTheme, primaryHover: value})}
                  description={uiText("Darker shade for hover states")}
                />

                <ColorPicker
                  label={uiText("Secondary Color")}
                  value={customTheme.secondary}
                  onChange={(value) => setCustomThemeLocal({...customTheme, secondary: value})}
                  description={uiText("Secondary accent color")}
                />

                <ColorPicker
                  label={uiText("Accent Color")}
                  value={customTheme.accent}
                  onChange={(value) => setCustomThemeLocal({...customTheme, accent: value})}
                  description={uiText("Used for information and highlights")}
                />

                <ColorPicker
                  label={uiText("Success Color")}
                  value={customTheme.success}
                  onChange={(value) => setCustomThemeLocal({...customTheme, success: value})}
                  description={uiText("Used for success messages and positive actions")}
                />

                <ColorPicker
                  label={uiText("Warning Color")}
                  value={customTheme.warning}
                  onChange={(value) => setCustomThemeLocal({...customTheme, warning: value})}
                  description={uiText("Used for warnings and caution messages")}
                />
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <Button onClick={handleApplyCustom} className="bg-dojo-primary hover:bg-dojo-primary-hover">
                  {uiText("Apply Custom Theme")}</Button>
                <Button variant="outline" onClick={handleResetToDefault}>
                  <RotateCcw className="h-4 w-4 mr-2" />
                  {uiText("Reset to Default")}</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ColorThemeSettings;
